import React, { useState } from 'react';
import ScoreGauge from './ScoreGauge';
import './AIAssessmentView.css';

/* ── helpers ──────────────────────────────────────────── */

const VERDICT_CONFIG = {
  approve: { label: 'Low Risk — Appears Qualified', bg: '#ECFDF3', border: '#BBF7D0', color: '#16A34A', icon: '✓' },
  review:  { label: 'Further Review Suggested', bg: '#FFF7ED', border: '#FCE7B2', color: '#92400E', icon: '⚠' },
  decline: { label: 'Elevated Risk — Review Carefully', bg: '#FEF2F2', border: '#FECACA', color: '#DC2626', icon: '⚠' }
};

function getRating(score) {
  if (score >= 760) return { text: 'Excellent', color: '#16A34A' };
  if (score >= 700) return { text: 'Good', color: '#22C55E' };
  if (score >= 650) return { text: 'Fair', color: '#EAB308' };
  if (score >= 550) return { text: 'Below avg', color: '#F59E0B' };
  return { text: 'Poor', color: '#EF4444' };
}

function getDtiRating(pct) {
  if (pct <= 20) return { text: 'Great', color: '#16A34A' };
  if (pct <= 35) return { text: 'Good', color: '#22C55E' };
  if (pct <= 50) return { text: 'Fair', color: '#F59E0B' };
  return { text: 'High', color: '#EF4444' };
}

function getRtiRating(pct) {
  if (pct <= 25) return { text: 'Great', color: '#16A34A' };
  if (pct <= 30) return { text: 'Good', color: '#22C55E' };
  if (pct <= 40) return { text: 'Fair', color: '#F59E0B' };
  return { text: 'High', color: '#EF4444' };
}

const CATEGORY_LABELS = {
  income: 'Income & Employment',
  references: 'References',
  credit: 'Credit & Screening'
};

const CATEGORY_DOC_TYPES = {
  income: ['employment_letter', 'paystub', 'bank_statement'],
  references: ['reference_letter'],
  credit: ['credit_report'] // Only used as fallback when no TU report
};

/* ── main component ──────────────────────────────────── */

function AIAssessmentView({
  assessment,
  application,
  tuReport,
  documents = [],
  onDownloadDoc,
  onApprove,
  onDecline,
  onUnapprove,
  applicationStatus,
  onRequestInfo,
  onRequestCategoryDoc,
  onRerunAssessment,
  onSwitchToManual,
  onPurchaseTu,
  aiCredits,
  onPurchaseCredits
}) {
  const [expandedSections, setExpandedSections] = useState({});
  const [requestingCategory, setRequestingCategory] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');

  if (!assessment) return null;

  const verdict = VERDICT_CONFIG[assessment.verdict] || VERDICT_CONFIG.review;
  const confidence = assessment.confidence_pct || 0;
  const reasoning = assessment.reviewer_summary || assessment.reasoning || '';
  const findings = typeof assessment.findings === 'object' ? assessment.findings : {};
  const crossRefs = Array.isArray(assessment.cross_references) ? assessment.cross_references : [];
  const creditSource = assessment.credit_source || 'none';
  const isPending = assessment.status === 'pending';

  // TU data for gauges
  const tuData = tuReport?.status === 'completed' ? tuReport.report_data : null;
  const tuScore = tuData?.score?.value;
  const tuDti = tuData?.incomeEstimator?.debtToIncomePercentage;
  const tuRti = tuData?.incomeEstimator?.rentToIncomePercentage;

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const categories = findings.categories || {};

  // Detect stale assessment (new docs uploaded after assessment)
  const assessmentDate = assessment.completed_at || assessment.created_at;
  const newDocsSinceAssessment = assessmentDate
    ? documents.filter(d => new Date(d.uploaded_at) > new Date(assessmentDate))
    : [];
  const isStale = newDocsSinceAssessment.length > 0;

  return (
    <div className="ai-assessment-view">

      {/* ── 1. Verdict Card ────────────────────────── */}
      <div
        className={`assessment-verdict-card assessment-verdict--${assessment.verdict || 'review'}`}
      >
        {isPending && (
          <div className="assessment-pending-bar">
            <span className="inline-spinner" aria-hidden="true" />
            <span>AI assessment in progress — results will update automatically.</span>
          </div>
        )}
        <div className="assessment-verdict-main">
          <span className="assessment-verdict-icon">{verdict.icon}</span>
          <div>
            <h2 className="assessment-verdict-label">{verdict.label}</h2>
            <div className="assessment-verdict-meta">
              <span className="assessment-confidence-badge">
                {confidence}% confidence
              </span>
            </div>
          </div>
        </div>
        {reasoning && (
          <p className="assessment-verdict-reasoning">{reasoning}</p>
        )}
        <p className="assessment-disclaimer">This is an AI-generated recommendation only. The final decision to approve or decline an applicant is yours.</p>
      </div>

      {/* ── Stale assessment banner ──────────────── */}
      {isStale && (
        <div className="assessment-stale-banner">
          <div>
            <span className="assessment-stale-banner-title">
              ⚠ {newDocsSinceAssessment.length} new document{newDocsSinceAssessment.length > 1 ? 's' : ''} since this assessment
            </span>
            <p className="assessment-stale-banner-sub">
              Updated results may change the verdict.
            </p>
          </div>
          {onRerunAssessment && (
            <button
              type="button"
              className="secondary-button"
              style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem', whiteSpace: 'nowrap' }}
              onClick={() => onRerunAssessment(newDocsSinceAssessment.length)}
              disabled={aiCredits && aiCredits.balance < 5}
            >
              🤖 Re-run (5 credits)
            </button>
          )}
        </div>
      )}

      {/* ── Re-run button (always visible when assessment exists and not stale) ──── */}
      {onRerunAssessment && !isStale && (
        <div className="ai-rerun-bar">
          <button
            type="button"
            className="ai-rerun-button"
            onClick={() => onRerunAssessment(0)}
            disabled={aiCredits && aiCredits.balance < 5}
          >
            🔄 Re-run AI Assessment (5 credits)
          </button>
          {aiCredits && aiCredits.balance < 5 && (
            <span className="ai-rerun-credits-low">
              {aiCredits.balance} credits remaining
              {onPurchaseCredits && (
                <> — <button type="button" className="link-button" onClick={onPurchaseCredits}>Top up</button></>
              )}
            </span>
          )}
        </div>
      )}

      {/* ── 2. Key Metrics Row ─────────────────────── */}
      {tuData && (
        <div className="assessment-metrics-row">
          {tuScore != null && (
            <ScoreGauge
              value={tuScore}
              minValue={tuData.score?.rangeMin || 300}
              maxValue={tuData.score?.rangeMax || 900}
              label="Credit Score"
              sublabel={`out of ${tuData.score?.rangeMax || 900}`}
              rating={getRating(tuScore).text}
              ratingColor={getRating(tuScore).color}
              size={160}
            />
          )}
          {tuDti != null && (
            <ScoreGauge
              value={Math.round(tuDti)}
              minValue={0}
              maxValue={100}
              label="Debt to Income"
              sublabel="As a % of monthly income"
              rating={getDtiRating(tuDti).text}
              ratingColor={getDtiRating(tuDti).color}
              size={160}
              invertColors
            />
          )}
          {tuRti != null && (
            <ScoreGauge
              value={Math.round(tuRti)}
              minValue={0}
              maxValue={100}
              label="Rent to Income"
              sublabel="As a % of monthly income"
              rating={getRtiRating(tuRti).text}
              ratingColor={getRtiRating(tuRti).color}
              size={160}
              invertColors
            />
          )}
          {application?.property_rent && (
            <div className="assessment-metric-card">
              <span className="assessment-metric-label">Monthly Rent</span>
              <span className="assessment-metric-value">${Number(application.property_rent).toLocaleString()}/mo</span>
            </div>
          )}
          {application?.income && (
            <div className="assessment-metric-card">
              <span className="assessment-metric-label">Stated Income</span>
              <span className="assessment-metric-value">${Number(application.income).toLocaleString()}/mo</span>
              {tuData?.incomeEstimator?.estimatedMonthlyIncome && (
                <span className="assessment-metric-sub">
                  TU est: {tuData.incomeEstimator.incomeRange || `$${tuData.incomeEstimator.estimatedMonthlyIncome}`}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── 3. Cross-References ────────────────────── */}
      {crossRefs.length > 0 && (
        <div className="assessment-section">
          <h3 className="assessment-section-title">Cross-References</h3>
          <div className="assessment-xref-list">
            {crossRefs.map((xr, i) => (
              <div key={i} className={`assessment-xref-item ${xr.match === true ? 'match' : xr.match === false ? 'mismatch' : 'neutral'}`}>
                <span className="assessment-xref-icon">
                  {xr.match === true ? '✓' : xr.match === false ? '✕' : '—'}
                </span>
                <div className="assessment-xref-body">
                  <span className="assessment-xref-field">{xr.field}</span>
                  <span className="assessment-xref-detail">
                    Stated: <strong>{xr.stated}</strong> · TransUnion: <strong>{xr.bureau}</strong>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 4. Section Findings ────────────────────── */}
      <div className="assessment-section">
        <h3 className="assessment-section-title">Document Analysis</h3>
        <p className="assessment-section-sub">
          {findings.documentCount || 0} document{(findings.documentCount || 0) !== 1 ? 's' : ''} analyzed
          {findings.scannedCount != null && findings.scannedCount < (findings.documentCount || 0) &&
            ` · ${findings.scannedCount} scanned`}
        </p>
        <div className="assessment-findings-cards">
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
            const catData = categories[key];
            if (!catData) return null;
            // catData can be a number (doc count) or object {summary, findings, riskLevel}
            const isRichData = typeof catData === 'object' && catData !== null;
            const catRiskLevel = isRichData ? (catData.riskLevel || 'low') : 'low';
            const catSummary = isRichData ? catData.summary : null;
            const catFindings = isRichData && Array.isArray(catData.findings) ? catData.findings : [];
            const isOpen = expandedSections[key];
            return (
              <div key={key} className="assessment-finding-card">
                <button
                  type="button"
                  className="assessment-finding-header"
                  onClick={() => toggleSection(key)}
                  aria-expanded={isOpen}
                >
                  <div className="assessment-finding-header-left">
                    <span className="assessment-finding-title">{label}</span>
                    <span className={`report-chip report-chip--${catRiskLevel === 'high' ? 'warn' : catRiskLevel === 'medium' ? 'pending' : 'success'}`}>
                      {catRiskLevel === 'low' ? '✓ Low risk' : catRiskLevel === 'medium' ? '⚠ Medium' : '⚠ High risk'}
                    </span>
                  </div>
                  <span className={`assessment-chevron ${isOpen ? 'open' : ''}`}>▾</span>
                </button>
                {isOpen && (() => {
                  const docTypes = CATEGORY_DOC_TYPES[key] || [];
                  // For credit: if TU report exists, ignore uploaded credit docs
                  const hasTuReport = tuReport?.status === 'completed';
                  const categoryDocs = (key === 'credit' && hasTuReport) ? [] : documents.filter(d => docTypes.includes(d.document_type));
                  return (
                    <div className="assessment-finding-body">
                      {catSummary && (
                        <p className="assessment-cat-summary">{catSummary}</p>
                      )}
                      {catFindings.length > 0 && (
                        <ul className="assessment-cat-findings">
                          {catFindings.map((f, i) => <li key={i}>{f}</li>)}
                        </ul>
                      )}
                      {!catSummary && catFindings.length === 0 && (
                        <p className="status-text">
                          {isPending ? 'AI scan in progress…' : 'No specific findings.'}
                        </p>
                      )}
                      {/* Credit: show TU status or uploaded docs as fallback */}
                      {key === 'credit' && hasTuReport && (
                        <div className="assessment-cat-docs">
                          <span className="assessment-cat-docs-status assessment-cat-docs-status--ok">✓ TransUnion report included in assessment</span>
                        </div>
                      )}
                      {/* Uploaded documents (non-credit, or credit fallback when no TU) */}
                      {categoryDocs.length > 0 && (
                        <div className="assessment-cat-docs">
                          <span className="assessment-cat-docs-label">
                            Documents ({categoryDocs.length})
                          </span>
                          <div className="assessment-cat-docs-list">
                            {categoryDocs.map(doc => (
                              <button
                                key={doc.id}
                                type="button"
                                className="assessment-doc-btn"
                                onClick={() => onDownloadDoc && onDownloadDoc(doc.id)}
                              >
                                <span className="assessment-doc-icon">📎</span>
                                <span className="assessment-doc-name">
                                  {doc.original_filename || doc.document_type}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {categoryDocs.length === 0 && !(key === 'credit' && hasTuReport) && (
                        <div className="assessment-cat-docs assessment-cat-docs--empty">
                          {key === 'credit' ? (
                            <span className="assessment-cat-docs-status assessment-cat-docs-status--warn">
                              No credit data — purchase a TransUnion report or upload a credit report
                              {onPurchaseTu && (
                                <button type="button" className="link-button assessment-cat-docs-action" onClick={onPurchaseTu}>
                                  Purchase →
                                </button>
                              )}
                            </span>
                          ) : (
                            <span className="assessment-cat-docs-status assessment-cat-docs-status--muted">No documents uploaded for this category</span>
                          )}
                        </div>
                      )}

                      {/* Request document action */}
                      <div className="assessment-cat-request">
                        {requestingCategory === key ? (
                          <div>
                            <textarea
                              className="assessment-cat-request-input"
                              value={requestMessage}
                              onChange={(e) => setRequestMessage(e.target.value)}
                              placeholder={`Tell the tenant what you need (e.g. "Please upload a clearer photo of your ID" or "Your paystub is from 6 months ago, please provide a recent one")`}
                            />
                            <div className="assessment-cat-request-actions">
                              <button
                                type="button"
                                className="primary-button assessment-cat-request-btn"
                                disabled={!requestMessage.trim()}
                                onClick={() => {
                                  if (onRequestCategoryDoc) {
                                    onRequestCategoryDoc(key, label, requestMessage.trim());
                                  }
                                  setRequestingCategory(null);
                                  setRequestMessage('');
                                }}
                              >
                                Send Request
                              </button>
                              <button
                                type="button"
                                className="ghost-button assessment-cat-request-btn"
                                onClick={() => { setRequestingCategory(null); setRequestMessage(''); }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="ghost-button assessment-cat-request-trigger"
                            onClick={() => {
                              // Pre-populate with AI-generated message based on findings
                              let prefill = '';
                              if (assessment.status === 'completed') {
                                if (catFindings.length > 0) {
                                  prefill = `Hi, we noticed some issues with your ${label.toLowerCase()} documents:\n\n${catFindings.slice(0, 3).map(f => `• ${f}`).join('\n')}\n\nCould you please upload updated documents to address these?`;
                                } else if (categoryDocs.length === 0) {
                                  prefill = `Hi, we're missing ${label.toLowerCase()} documents for your application. Could you please upload the required documents?`;
                                } else {
                                  prefill = `Hi, could you please provide updated ${label.toLowerCase()} documents for your application?`;
                                }
                              }
                              setRequestMessage(prefill);
                              setRequestingCategory(key);
                            }}
                          >
                            📩 Request {categoryDocs.length === 0 ? 'document' : 'updated document'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 5. Credit Source Indicator ─────────────── */}
      <div className="assessment-section">
        <h3 className="assessment-section-title">Credit Data Source</h3>
        <div className="assessment-credit-source">
          {creditSource === 'tu_report' && (
            <div className="assessment-source-badge assessment-source-badge--tu">
              <span>📊</span>
              <div>
                <strong>TransUnion Report</strong>
                <p>Official bureau data including score, tradelines, collections, and public records.</p>
              </div>
            </div>
          )}
          {creditSource === 'uploaded_pdf' && (
            <div className="assessment-source-badge assessment-source-badge--pdf">
              <span>📄</span>
              <div>
                <strong>Uploaded Credit Report (PDF)</strong>
                <p>AI-scanned from tenant-uploaded document. For higher confidence, consider adding a TU report.</p>
                {onPurchaseTu && (
                  <button className="secondary-button" type="button" onClick={onPurchaseTu} style={{ marginTop: '0.5rem', fontSize: '0.82rem' }}>
                    Add TU Report (+$29.99)
                  </button>
                )}
              </div>
            </div>
          )}
          {creditSource === 'none' && (
            <div className="assessment-source-badge assessment-source-badge--none">
              <span>❌</span>
              <div>
                <strong>No Credit Data</strong>
                <p>No credit report available for this applicant.</p>
                {onPurchaseTu && (
                  <button className="secondary-button" type="button" onClick={onPurchaseTu} style={{ marginTop: '0.5rem', fontSize: '0.82rem' }}>
                    Purchase TU Report ($29.99)
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── 6. Bottom Actions ─────────────────────── */}
      <div className="assessment-actions">
        {applicationStatus === 'approved' && (
          <div className="assessment-status-banner assessment-status-banner--approved">
            <div>
              <span className="assessment-status-banner-title">✓ Application Approved</span>
              <p className="assessment-status-banner-sub">
                Proceed to Lease & Signing to finalize.
              </p>
            </div>
            {onUnapprove && (
              <button type="button" className="ghost-button assessment-status-banner-action"
                onClick={onUnapprove}>
                Undo Approval
              </button>
            )}
          </div>
        )}
        {applicationStatus === 'rejected' && (
          <div className="assessment-status-banner assessment-status-banner--rejected">
            <span className="assessment-status-banner-title">✕ Application Declined</span>
          </div>
        )}
        {onApprove && (
          <button className="primary-button assessment-action--approve" type="button" onClick={onApprove} disabled={isPending}>
            Approve Application
          </button>
        )}
        {onDecline && (
          <button className="ghost-button assessment-action--decline" type="button" onClick={onDecline} disabled={isPending}>
            Decline
          </button>
        )}

        {onSwitchToManual && (
          <button className="ghost-button" type="button" onClick={onSwitchToManual}>
            Switch to Manual Review
          </button>
        )}
      </div>
    </div>
  );
}

export default AIAssessmentView;
